import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";
import Text, { TextVariant } from "@/components/ui/text";
import { Bullet, Close } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";

const chipVariants = cva(
  "group inline-flex items-center w-fit max-w-full rounded-lg border-[0.5px] transition-all duration-200 ease-in-out outline-none",
  {
    variants: {
      size: {
        sm: "h-6 gap-1 px-[6px] py-1",
        md: "h-7 gap-1.5 px-2 py-1",
        lg: "h-8 gap-[6px] px-2 py-[6px]"
      },
      filled: {
        true: "bg-badges-default border-badge-default",
        false: "bg-transparent border-transparent"
      },
      ghost: {
        true: "border-[2px] border-dashed border-badge-default bg-transparent",
        false: ""
      },
      preview: {
        true: "!bg-transparent !border-transparent",
        false: ""
      },
      interactive: {
        true: "cursor-pointer hover:bg-surface-hover-soft active:bg-surface-pressed",
        false: ""
      },
      disabled: {
        true: "cursor-not-allowed opacity-50 hover:!bg-transparent active:!bg-transparent",
        false: ""
      }
    },
    defaultVariants: {
      size: "sm",
      filled: false,
      ghost: false,
      preview: false,
      interactive: false,
      disabled: false
    }
  }
);

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    VariantProps<typeof chipVariants> {
  label?: string;
  icon?: React.ReactNode;
  avatar?: React.ReactNode;
  statusColor?: string;
  counter?: number;
  selected?: boolean;
  removable?: boolean;
  removeAriaLabel?: string;
  onRemove?: (event: React.MouseEvent) => void;
  textVariant?: TextVariant;
  textClassName?: string;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      label,
      icon,
      avatar,
      statusColor,
      counter,
      size = "sm",
      filled = false,
      ghost = false,
      preview = false,
      selected = false,
      disabled = false,
      removable = false,
      removeAriaLabel,
      onRemove,
      textVariant,
      textClassName,
      className,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const interactive = Boolean(onClick) && !disabled && !preview;

    const handleRemove = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onRemove?.(event);
    };

    return (
      <div
        ref={ref}
        className={cn(
          chipVariants({
            size,
            filled,
            ghost,
            preview,
            interactive,
            disabled
          }),
          selected && "bg-badges-selected border-border-badge-selected",
          className
        )}
        data-disabled={disabled}
        data-selected={selected}
        onClick={disabled ? undefined : onClick}
        {...props}
      >
        {statusColor && (
          <IconContainer size="xs" style={{ color: statusColor }}>
            <Bullet />
          </IconContainer>
        )}
        {avatar}
        {icon}
        {children ?? (
          <Text
            variant={textVariant ?? (size === "lg" ? "body" : "smBody")}
            hasTruncatedTooltip
            className={cn(
              "truncate",
              !filled && !selected && "text-text-body-tertiary",
              disabled && "text-disabled",
              textClassName
            )}
          >
            {label}
          </Text>
        )}
        {counter !== undefined && (
          <Text
            variant="smBodySecondary"
            className="flex-shrink-0 text-text-body-secondary"
          >
            {counter}
          </Text>
        )}
        {removable && !disabled && (
          <IconButton
            type="button"
            variant="iconNoBg"
            size="xs"
            aria-label={removeAriaLabel}
            onClick={handleRemove}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          >
            <Close />
          </IconButton>
        )}
      </div>
    );
  }
);
Chip.displayName = "Chip";

export default Chip;
